import { useMemo, type ReactNode } from "react";
import { useFreelancerTimeline, type TimelineSegment } from "@/hooks/useDisponibilidade";
import { EIXO_LABEL, axisValueLabel, fonteLabel } from "@/utils/disponibilidadeUtils";
import { formatUtcDate } from "@/utils/dateUtils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// Cor de fundo por valor de eixo (barras da timeline). Sem jargão nos títulos.
const VALUE_BACKGROUND: Record<string, string> = {
  // situacao_emprego
  empregado_fixo: "bg-blue-400",
  freela_ativo: "bg-emerald-400",
  entre_trabalhos: "bg-slate-300",
  // interesse_freela
  topa_freela: "bg-emerald-500",
  aberto_mas_ocupado: "bg-amber-400",
  nao_topa: "bg-rose-400",
  // relacao_conosco
  trabalhando_conosco: "bg-blue-500",
  ja_trabalhou_conosco: "bg-slate-400",
  disponivel_em_breve: "bg-teal-400",
  nunca_trabalhou: "bg-slate-200",
  desconhecido: "bg-slate-200",
};

const DIMENSION_LANES = ["situacao_emprego", "interesse_freela", "relacao_conosco"] as const;

function toMillis(dateString: string) {
  return new Date(dateString).getTime();
}

/** Barra da timeline com tooltip (evento por evento) ao passar o mouse. */
function BarSegment({
  className,
  style,
  tooltip,
}: {
  className: string;
  style: React.CSSProperties;
  tooltip: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={className} style={style} />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px]">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export function DisponibilidadeTimeline({
  freelancerId,
  monthsBack = 18,
  compact = false,
  legendAction,
}: {
  freelancerId?: string | null;
  monthsBack?: number;
  /** Versão enxuta pra colunas estreitas (rótulos curtos, sem texto de fonte). */
  compact?: boolean;
  /** Ação opcional exibida à direita da legenda (ex.: abrir histórico completo). */
  legendAction?: ReactNode;
}) {
  const { data, isLoading } = useFreelancerTimeline(freelancerId, monthsBack);
  const laneLabelClass = compact
    ? "w-14 shrink-0 text-[9px] leading-tight text-muted-foreground text-right"
    : "w-[104px] shrink-0 text-[11px] text-muted-foreground text-right";

  const layout = useMemo(() => {
    if (!data || !data.segments?.length) return null;
    const windowStartMs = toMillis(data.window.from);
    const windowEndMs = toMillis(data.window.to);
    const totalMs = Math.max(windowEndMs - windowStartMs, 1);
    const nowPercent = ((toMillis(data.now) - windowStartMs) / totalMs) * 100;

    const segmentPosition = (segment: TimelineSegment) => {
      const segmentStartMs = toMillis(segment.from);
      const segmentEndMs = segment.to ? toMillis(segment.to) : windowEndMs;
      const leftPercent = ((Math.max(segmentStartMs, windowStartMs) - windowStartMs) / totalMs) * 100;
      const widthPercent =
        ((Math.min(segmentEndMs, windowEndMs) - Math.max(segmentStartMs, windowStartMs)) / totalMs) * 100;
      return { leftPercent, widthPercent: Math.max(widthPercent, 0.4) };
    };
    return { segments: data.segments, segmentPosition, nowPercent };
  }, [data]);

  if (isLoading) {
    return <div className="text-xs text-muted-foreground">Carregando histórico...</div>;
  }
  if (!layout) {
    return (
      <div className="text-xs text-muted-foreground">
        Sem histórico de disponibilidade ainda. Vale perguntar.
      </div>
    );
  }

  const formatMonth = (dateString: string) =>
    new Date(dateString).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });

  const periodo = (segment: TimelineSegment) =>
    `${formatUtcDate(segment.from)} até ${segment.to ? formatUtcDate(segment.to) : "hoje"}`;

  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-2">
        {/* eixo de tempo — alinhado às barras por um espaçador do tamanho do rótulo */}
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <span className={cn(laneLabelClass, "text-muted-foreground/0")} aria-hidden />
          <div className="flex flex-1 justify-between">
            <span>{formatMonth(data!.window.from)}</span>
            <span>hoje</span>
          </div>
        </div>

        {DIMENSION_LANES.map((dimension) => (
          <div key={dimension} className="flex items-center gap-2">
            <span className={laneLabelClass}>{EIXO_LABEL[dimension]}</span>
            <div className="relative h-5 flex-1 rounded bg-slate-50 overflow-hidden">
              {layout.segments.map((segment, index) => {
                const axisInfo = segment.eixos?.[dimension];
                const { leftPercent, widthPercent } = layout.segmentPosition(segment);
                const withoutInfo = !axisInfo?.value || axisInfo.value === "desconhecido";
                return (
                  <BarSegment
                    key={index}
                    className={cn(
                      "absolute top-0 h-full",
                      withoutInfo
                        ? "bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,#e2e8f0_3px,#e2e8f0_5px)]"
                        : VALUE_BACKGROUND[axisInfo!.value] ?? "bg-slate-300"
                    )}
                    style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
                    tooltip={
                      <div className="space-y-0.5 text-xs">
                        <div className="font-medium text-muted-foreground">{EIXO_LABEL[dimension]}</div>
                        {withoutInfo ? (
                          <div>Sem info nesse período</div>
                        ) : (
                          <div className="font-medium">
                            {axisValueLabel(dimension, axisInfo!.value)}
                            {axisInfo!.source && (
                              <span className="font-normal text-muted-foreground">
                                {" "}· {fonteLabel(axisInfo!.source)}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-muted-foreground">{periodo(segment)}</div>
                      </div>
                    }
                  />
                );
              })}
              {/* marcador "hoje" */}
              <div
                className="absolute top-0 h-full w-px bg-foreground/40"
                style={{ left: `${layout.nowPercent}%` }}
              />
            </div>
          </div>
        ))}

        {/* faixa de carga (ocupação) */}
        <div className="flex items-center gap-2">
          <span className={laneLabelClass}>Ocupação</span>
          <div className="relative h-5 flex-1 rounded bg-slate-50 overflow-hidden">
            {layout.segments.map((segment, index) => {
              const cargaAtual = segment.carga?.carga ?? 0;
              if (cargaAtual <= 0) return null;
              const { leftPercent, widthPercent } = layout.segmentPosition(segment);
              return (
                <BarSegment
                  key={index}
                  className={cn(
                    "absolute top-0 h-full",
                    cargaAtual >= 2 ? "bg-indigo-500" : "bg-indigo-300"
                  )}
                  style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
                  tooltip={
                    <div className="space-y-0.5 text-xs">
                      <div className="font-medium">
                        {cargaAtual} projeto{cargaAtual > 1 ? "s" : ""} em paralelo
                      </div>
                      <div className="text-muted-foreground">{periodo(segment)}</div>
                    </div>
                  }
                />
              );
            })}
            <div
              className="absolute top-0 h-full w-px bg-foreground/40"
              style={{ left: `${layout.nowPercent}%` }}
            />
          </div>
        </div>

        {/* legenda: quadrado hachurado + explicação (+ ação opcional à direita) */}
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground/80">
          <span
            className="inline-block h-3 w-3 shrink-0 rounded-sm border border-slate-200 bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,#e2e8f0_2px,#e2e8f0_4px)]"
            aria-hidden
          />
          <span>Sem info no período</span>
          {legendAction && <div className="ml-auto">{legendAction}</div>}
        </div>
      </div>
    </TooltipProvider>
  );
}

export default DisponibilidadeTimeline;
